import {
  trackSelfDescribingEvent,
  newTracker,
  type BrowserTracker,
} from "@snowplow/browser-tracker";
import { localStorageAccessible } from "@snowplow/browser-tracker-core";
import rollbar from "../rollbar.client";

interface Event {
  name: string;
  data: any;
  schema?: string;
}

const GENERIC_SCHEMA = "iglu:io.greenhouse/generic-event/jsonschema/1-0-0";

export class SnowplowTracker {
  private tracker: BrowserTracker | null | undefined = null;

  track(event: Event) {
    try {
      if (!this.enabled) {
        return;
      }

      this.lazyInit();

      this.logEvent(event);

      trackSelfDescribingEvent({
        event: {
          schema: event.schema || GENERIC_SCHEMA,
          data: {
            ...event.data,
            event_name: event.name,
          },
        },
      });
    } catch (e: any) {
      // Initializing the tracker requires crypto.randomUUID to be available.
      // It isn't in older browsers, and the fallback provided by uuid doesn't seem to work.
      // This is a workaround to avoid logging rollbars in this scenario.
      if (e instanceof TypeError && e.message?.includes("randomUUID")) {
        return;
      }

      // We should not blow up if we fail to track an event, but we should log to rollbar
      rollbar.error(e);
    }
  }

  // Lazily initialize the tracker to avoid trying to access window.ENV during SSR
  private lazyInit() {
    if (this.initialized) {
      return;
    }

    this.tracker = newTracker(window.ENV.SNOWPLOW_NAMESPACE, window.ENV.SNOWPLOW_ENDPOINT, {
      appId: window.ENV.SNOWPLOW_APP_ID,
      platform: "web",
      respectDoNotTrack: true,
      bufferSize: 1,
      encodeBase64: false,
      eventMethod: "post",
      useLocalStorage: localStorageAccessible(),
      anonymousTracking: { withServerAnonymisation: true },
      stateStorageStrategy: "none",
    });
  }

  private get enabled(): boolean {
    return window.ENV.SNOWPLOW_ENABLED || false;
  }

  private get initialized(): boolean {
    return !!this.tracker;
  }

  private logEvent(event: Event) {
    if (window.ENV.SNOWPLOW_DEBUG) {
      console.info("Emitting Snowplow Event:", event);
    }
  }
}
